
import React, { useState, useEffect } from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext 
} from "@/components/ui/carousel";
import { useIsMobile } from '@/hooks/use-mobile';
import useEmblaCarousel from 'embla-carousel-react';
import AutoplayPlugin from 'embla-carousel-autoplay';
import { ScrollArea } from "@/components/ui/scroll-area";
import { HoverCard, HoverCardTrigger, HoverCardContent } from "@/components/ui/hover-card";

type FoodItemCategory = 'Burgers' | 'Pizza' | 'Desserts' | 'Sides' | 'Drinks' | 'All';

interface FoodItem {
  id: number;
  imageUrl: string;
  title: string;
  description: string;
  height: 'tall' | 'medium' | 'short';
  category: FoodItemCategory;
}

const FoodGallery = () => {
  const isMobile = useIsMobile();
  const [autoplay, setAutoplay] = useState(true);
  
  const foodItems: FoodItem[] = [
    {
      id: 1,
      imageUrl: 'https://images.unsplash.com/photo-1568901346375-23c9450c58cd',
      title: 'Gourmet Burger',
      description: 'Hand-crafted premium beef burger with artisanal toppings',
      height: 'tall',
      category: 'Burgers',
    },
    {
      id: 2,
      imageUrl: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38',
      title: 'Neapolitan Pizza',
      description: 'Authentic Italian pizza with San Marzano tomatoes and fresh mozzarella',
      height: 'medium',
      category: 'Pizza',
    },
    {
      id: 3,
      imageUrl: 'https://images.unsplash.com/photo-1541592106381-b31e9677c0e5',
      title: 'Crispy French Fries',
      description: 'Golden crispy fries served with specialty dipping sauces',
      height: 'short',
      category: 'Sides',
    },
    {
      id: 4,
      imageUrl: 'https://images.unsplash.com/photo-1513104890138-7c749659a591',
      title: 'Pepperoni Pizza',
      description: 'Classic pepperoni pizza with our signature tomato sauce',
      height: 'tall',
      category: 'Pizza',
    },
    {
      id: 5,
      imageUrl: 'https://images.unsplash.com/photo-1550547660-d9450f859349',
      title: 'Loaded Burger',
      description: 'Stacked burger with all the fixings, bacon and special sauce',
      height: 'short',
      category: 'Burgers',
    },
    {
      id: 6,
      imageUrl: 'https://plus.unsplash.com/premium_photo-1683121324272-90f4b4084ac9?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      title: 'Onion Rings',
      description: 'Crispy battered onion rings with a touch of seasoning',
      height: 'medium',
      category: 'Sides',
    },
    {
      id: 7,
      imageUrl: 'https://images.unsplash.com/photo-1617720049168-a95f731b6581?q=80&w=3107&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      title: 'Grilled Sandwich',
      description: 'Toasted sandwich with premium cheeses and fillings',
      height: 'tall',
      category: 'Sides',
    },
    {
      id: 8,
      imageUrl: 'https://images.unsplash.com/photo-1627662168223-7df99068099a',
      title: 'Breakfast Platter',
      description: 'Complete breakfast with eggs, bacon, and toast',
      height: 'medium',
      category: 'Sides',
    },
    {
      id: 9,
      imageUrl: 'https://images.unsplash.com/photo-1528736235302-52922df5c122',
      title: 'Ice Cream Sundae',
      description: 'Decadent ice cream sundae with rich toppings',
      height: 'short',
      category: 'Desserts',
    },
    {
      id: 10,
      imageUrl: 'https://images.unsplash.com/photo-1554502078-ef0fc409efce',
      title: 'Loaded Nachos',
      description: 'Fully loaded nachos with cheese, jalapeños and sour cream',
      height: 'medium',
      category: 'Sides',
    },
    {
      id: 11,
      imageUrl: 'https://plus.unsplash.com/premium_photo-1669742928112-19364a33b530?q=80&w=3087&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D',
      title: 'Chicken Wings',
      description: 'Spicy buffalo wings served with celery and blue cheese dip',
      height: 'tall',
      category: 'Sides',
    },
    {
      id: 12,
      imageUrl: 'https://images.unsplash.com/photo-1619096252214-ef06c45683e3',
      title: 'Pasta Dish',
      description: 'Homemade pasta with authentic Italian sauce',
      height: 'medium',
      category: 'Sides',
    },
    {
      id: 13,
      imageUrl: 'https://images.unsplash.com/photo-1529411081224-84ac0d0bf6ab',
      title: 'Brownie Dessert',
      description: 'Rich chocolate brownie with vanilla ice cream',
      height: 'short',
      category: 'Desserts',
    },
    {
      id: 14,
      imageUrl: 'https://images.unsplash.com/photo-1561758033-d89a9ad46330',
      title: 'Milkshake',
      description: 'Creamy vanilla milkshake with whipped cream topping',
      height: 'tall',
      category: 'Drinks',
    },
    {
      id: 15,
      imageUrl: 'https://images.unsplash.com/photo-1603532648955-039310d9ed75',
      title: 'Tacos',
      description: 'Authentic street tacos with fresh salsa and lime',
      height: 'short',
      category: 'Sides',
    },
  ];

  // Setup autoplay plugin for carousel
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, dragFree: true, containScroll: 'trimSnaps' },
    [AutoplayPlugin({ delay: 4000, stopOnInteraction: true })]
  );

  // Pause carousel on hover
  const handleMouseEnter = () => {
    setAutoplay(false);
    emblaApi?.plugins().autoplay?.stop();
  };

  const handleMouseLeave = () => {
    setAutoplay(true);
    emblaApi?.plugins().autoplay?.play();
  };

  // Helper function to get aspect ratio based on height
  const getAspectRatio = (height: string) => {
    switch (height) {
      case 'tall':
        return 2/3;
      case 'medium':
        return 1;
      case 'short':
        return 16/9;
      default:
        return 1;
    }
  };

  // Mobile view with Pinterest-style masonry layout
  if (isMobile) {
    // Split items into two columns for Pinterest-style layout
    const leftColumnItems = foodItems.filter((_, index) => index % 2 === 0);
    const rightColumnItems = foodItems.filter((_, index) => index % 2 === 1);

    return (
      <section className="bg-grow99x-lightBg py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 gap-2">
            {/* Left column */}
            <div className="flex flex-col gap-2">
              {leftColumnItems.map((item) => (
                <Card key={item.id} className="overflow-hidden shadow-md">
                  <CardContent className="p-0 relative">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title} 
                      className="w-full h-auto" 
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 touch-none active:opacity-100 flex flex-col justify-end p-2 text-white">
                      <h3 className="font-semibold text-sm">{item.title}</h3>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Right column */}
            <div className="flex flex-col gap-2">
              {rightColumnItems.map((item) => (
                <Card key={item.id} className="overflow-hidden shadow-md">
                  <CardContent className="p-0 relative">
                    <img 
                      src={item.imageUrl} 
                      alt={item.title} 
                      className="w-full h-auto" 
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 touch-none active:opacity-100 flex flex-col justify-end p-2 text-white">
                      <h3 className="font-semibold text-sm">{item.title}</h3>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Desktop view with masonry layout
  return (
    <section className="bg-grow99x-lightBg py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Auto-scrolling carousel for featured items */}
        <div className="mb-12">
          <Carousel
            className="w-full"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            <CarouselContent ref={emblaRef}>
              {foodItems.slice(0, 6).map((item) => (
                <CarouselItem key={item.id} className="basis-1/4">
                  <Card className="shadow-md group overflow-hidden">
                    <CardContent className="p-0 relative">
                      <AspectRatio ratio={16/9}>
                        <img
                          src={item.imageUrl}
                          alt={item.title}
                          className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
                          <h3 className="font-semibold text-lg">{item.title}</h3>
                          <p className="text-sm mt-1">{item.description}</p>
                        </div>
                      </AspectRatio>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="left-2" />
            <CarouselNext className="right-2" />
          </Carousel>
        </div>

        {/* Masonry layout for all items */}
        <div className="columns-1 sm:columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4">
          {foodItems.map((item) => (
            <div key={item.id} className="break-inside-avoid mb-4">
              <HoverCard>
                <HoverCardTrigger asChild>
                  <Card className="overflow-hidden shadow-md hover:shadow-lg transition-all duration-300 group">
                    <CardContent className="p-0">
                      <AspectRatio ratio={getAspectRatio(item.height)}>
                        <img 
                          src={item.imageUrl} 
                          alt={item.title} 
                          className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-4 text-white">
                          <h3 className="font-semibold text-lg">{item.title}</h3>
                          <p className="text-sm mt-1">{item.description}</p>
                        </div>
                      </AspectRatio>
                    </CardContent>
                  </Card>
                </HoverCardTrigger>
                <HoverCardContent className="w-80">
                  <div className="flex flex-col space-y-2">
                    <h4 className="text-lg font-semibold">{item.title}</h4>
                    <p className="text-sm text-muted-foreground">{item.description}</p>
                    <p className="text-xs text-muted-foreground">Category: {item.category}</p>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FoodGallery;
